import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Progress } from '../ui/progress';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ScatterChart,
  Scatter
} from 'recharts';
import { 
  Download, 
  Filter,
  Activity,
  Zap,
  Clock,
  Users,
  Globe,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Settings,
  Play,
  Pause,
  MoreHorizontal
} from 'lucide-react';
import { AgentActivityMetrics, AnalyticsTimeRange, ExportOptions } from '../../types/AnalyticsTypes';

interface AgentActivityReportProps {
  data: AgentActivityMetrics;
  dateRange: AnalyticsTimeRange;
  onExport: (options: ExportOptions) => void;
}

export default function AgentActivityReport({ data, dateRange, onExport }: AgentActivityReportProps) {
  const [selectedView, setSelectedView] = useState<'overview' | 'performance' | 'usage' | 'geographic'>('overview');

  const getStatusColor = (status: 'active' | 'inactive' | 'maintenance') => {
    switch (status) {
      case 'active':
        return 'bg-verified-green text-white';
      case 'inactive':
        return 'bg-warm-slate text-white';
      case 'maintenance':
        return 'bg-amber-500 text-white';
    }
  };

  const getStatusIcon = (status: 'active' | 'inactive' | 'maintenance') => {
    switch (status) {
      case 'active':
        return <Play className="h-3 w-3" />;
      case 'inactive':
        return <Pause className="h-3 w-3" />;
      case 'maintenance':
        return <Settings className="h-3 w-3" />;
    }
  };

  const getUtilizationColor = (score: number) => {
    if (score >= 80) return 'text-verified-green';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getLoadColor = (load: 'low' | 'medium' | 'high') => {
    switch (load) {
      case 'low':
        return 'bg-verified-green';
      case 'medium':
        return 'bg-amber-500';
      case 'high':
        return 'bg-red-500';
    }
  };

  const handleExport = () => {
    onExport({
      format: 'pdf',
      includeCharts: true,
      includeRawData: true,
      dateRange,
      reportTypes: ['agents']
    });
  };

  // Prepare performance vs usage data for scatter plot
  const performanceUsageData = data.activityByAgent.map(agent => ({
    name: agent.agentName,
    interactions: agent.interactions,
    successRate: agent.successRate,
    responseTime: agent.averageResponseTime,
    utilization: agent.utilizationScore
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-display text-charcoal-ink">Agent Activity Report</h2>
          <p className="text-warm-slate">
            Comprehensive analysis of agent performance, usage patterns, and optimization opportunities
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button onClick={handleExport} size="sm" className="credential-capital-button-primary">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Total Agents</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.totalAgents}
                </p>
                <div className="flex items-center mt-2">
                  <Badge variant="outline" className="text-xs">
                    Active: {data.activeAgents}
                  </Badge>
                </div>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Zap className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Total Interactions</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.totalInteractions.toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Activity className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Avg Response Time</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {data.averageResponseTime}s
                </p>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Clock className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Avg Success Rate</p>
                <p className="text-2xl font-semibold text-verified-green">
                  {(data.activityByAgent.reduce((sum, agent) => sum + agent.successRate, 0) / data.activityByAgent.length).toFixed(1)}%
                </p>
              </div>
              <div className="p-3 rounded-full bg-verified-green/10">
                <CheckCircle className="h-6 w-6 text-verified-green" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Selector */}
      <Card className="credential-capital-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-display text-charcoal-ink">Agent Performance Analysis</CardTitle>
              <CardDescription>
                Detailed insights into agent activity, performance metrics, and usage patterns
              </CardDescription>
            </div>
            <div className="flex space-x-2">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'performance', label: 'Performance' },
                { key: 'usage', label: 'Usage Patterns' },
                { key: 'geographic', label: 'Geographic' }
              ].map(view => (
                <Button
                  key={view.key}
                  variant={selectedView === view.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedView(view.key as any)}
                >
                  {view.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {selectedView === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Interaction Trends */}
                <div>
                  <h4 className="font-medium text-charcoal-ink mb-4">Daily Interaction Trends</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={data.interactionTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(value) => new Date(value).toLocaleDateString()}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="interactions" 
                        stroke="#3B82F6" 
                        fill="#3B82F6" 
                        fillOpacity={0.8}
                        name="Total Interactions"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="successfulInteractions" 
                        stroke="#10B981" 
                        fill="#10B981" 
                        fillOpacity={0.6}
                        name="Successful Interactions"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Response Time Trends */}
                <div>
                  <h4 className="font-medium text-charcoal-ink mb-4">Response Time Trends</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.interactionTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(value) => new Date(value).toLocaleDateString()}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(value) => new Date(value).toLocaleDateString()}
                        formatter={(value: any) => [`${value.toFixed(2)}s`, 'Response Time']}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="averageResponseTime" 
                        stroke="#FBBF24" 
                        strokeWidth={3}
                        dot={{ fill: '#FBBF24', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top Performing Agents */}
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Top Performing Agents</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Interactions</TableHead>
                      <TableHead>Success Rate</TableHead>
                      <TableHead>Response Time</TableHead>
                      <TableHead>Utilization</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.activityByAgent.slice(0, 10).map((agent) => (
                      <TableRow key={agent.agentId}>
                        <TableCell className="font-medium">{agent.agentName}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(agent.status)}>
                            <div className="flex items-center space-x-1">
                              {getStatusIcon(agent.status)}
                              <span>{agent.status}</span>
                            </div>
                          </Badge>
                        </TableCell>
                        <TableCell>{agent.interactions.toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Progress value={agent.successRate} className="flex-1" />
                            <span className="text-sm font-medium">{agent.successRate.toFixed(1)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>{agent.averageResponseTime.toFixed(2)}s</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <span className={`font-medium ${getUtilizationColor(agent.utilizationScore)}`}>
                              {agent.utilizationScore}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {selectedView === 'performance' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Performance vs Usage Analysis</h4>
                <ResponsiveContainer width="100%" height={400}>
                  <ScatterChart data={performanceUsageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="interactions" 
                      name="Interactions"
                      type="number"
                    />
                    <YAxis 
                      dataKey="successRate" 
                      name="Success Rate"
                      domain={[0, 100]}
                    />
                    <Tooltip 
                      cursor={{ strokeDasharray: '3 3' }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 border rounded-lg shadow-lg">
                              <p className="font-medium">{data.name}</p>
                              <p className="text-sm">Interactions: {data.interactions?.toLocaleString()}</p>
                              <p className="text-sm">Success Rate: {data.successRate?.toFixed(1)}%</p>
                              <p className="text-sm">Response Time: {data.responseTime?.toFixed(2)}s</p>
                              <p className="text-sm">Utilization: {data.utilization}%</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter 
                      dataKey="successRate" 
                      fill="#3B82F6"
                    />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Performance Distribution */}
                <div>
                  <h4 className="font-medium text-charcoal-ink mb-4">Success Rate Distribution</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={data.activityByAgent.map(agent => ({
                        name: agent.agentName.length > 15 ? 
                          agent.agentName.substring(0, 15) + '...' : 
                          agent.agentName,
                        successRate: agent.successRate
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Bar dataKey="successRate" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Response Time Distribution */}
                <div>
                  <h4 className="font-medium text-charcoal-ink mb-4">Response Time Distribution</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={data.activityByAgent.map(agent => ({
                        name: agent.agentName.length > 15 ? 
                          agent.agentName.substring(0, 15) + '...' : 
                          agent.agentName,
                        responseTime: agent.averageResponseTime
                      }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip formatter={(value: any) => [`${value.toFixed(2)}s`, 'Response Time']} />
                      <Bar dataKey="responseTime" fill="#FBBF24" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {selectedView === 'usage' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Peak Usage Hours</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.peakUsageHours}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="hour" 
                      tickFormatter={(value) => `${value}:00`}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => `${value}:00`}
                      formatter={(value: any, name: any) => [value.toLocaleString(), 'Interactions']}
                    />
                    <Bar 
                      dataKey="interactions" 
                      fill={(entry: any) => {
                        const load = data.peakUsageHours[entry]?.load;
                        return load === 'high' ? '#EF4444' : load === 'medium' ? '#FBBF24' : '#10B981';
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Load Distribution */}
                <div>
                  <h4 className="font-medium text-charcoal-ink mb-4">System Load Distribution</h4>
                  <div className="space-y-3">
                    {['high', 'medium', 'low'].map(loadLevel => {
                      const count = data.peakUsageHours.filter(hour => hour.load === loadLevel).length;
                      const percentage = (count / data.peakUsageHours.length) * 100;
                      return (
                        <div key={loadLevel} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${getLoadColor(loadLevel as any)}`} />
                            <span className="capitalize">{loadLevel} Load</span>
                          </div>
                          <div className="flex items-center space-x-4">
                            <Progress value={percentage} className="w-24" />
                            <span className="text-sm font-medium w-12">{count}h</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Usage Recommendations */}
                <div>
                  <h4 className="font-medium text-charcoal-ink mb-4">Optimization Recommendations</h4>
                  <div className="space-y-3">
                    <Card className="border border-amber-200 bg-amber-50">
                      <CardContent className="pt-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <span className="font-medium text-amber-800">Peak Load Warning</span>
                        </div>
                        <p className="text-sm text-amber-700">
                          {data.peakUsageHours.filter(h => h.load === 'high').length} hours with high load detected. 
                          Consider load balancing during peak hours.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border border-blue-200 bg-blue-50">
                      <CardContent className="pt-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <TrendingUp className="h-4 w-4 text-blue-600" />
                          <span className="font-medium text-blue-800">Scaling Opportunity</span>
                        </div>
                        <p className="text-sm text-blue-700">
                          {data.activityByAgent.filter(a => a.utilizationScore > 85).length} agents 
                          showing high utilization. Consider scaling resources.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedView === 'geographic' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Geographic Usage Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.geographicUsage}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="location" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="interactions" fill="#3B82F6" name="Interactions" />
                    <Bar dataKey="uniqueUsers" fill="#10B981" name="Unique Users" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h4 className="font-medium text-charcoal-ink mb-4">Regional Performance Metrics</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Region</TableHead>
                      <TableHead>Total Interactions</TableHead>
                      <TableHead>Unique Users</TableHead>
                      <TableHead>Success Rate</TableHead>
                      <TableHead>Performance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.geographicUsage.map((region) => (
                      <TableRow key={region.location}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Globe className="h-4 w-4 text-electric-steel" />
                            <span className="font-medium">{region.location}</span>
                          </div>
                        </TableCell>
                        <TableCell>{region.interactions.toLocaleString()}</TableCell>
                        <TableCell>{region.uniqueUsers.toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Progress value={region.successRate} className="flex-1" />
                            <span className="text-sm font-medium">{region.successRate.toFixed(1)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={region.successRate >= 90 ? 'default' : region.successRate >= 85 ? 'secondary' : 'destructive'}
                          >
                            {region.successRate >= 90 ? 'Excellent' : region.successRate >= 85 ? 'Good' : 'Needs Attention'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}